import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Buildings, FloppyDisk } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const CompanyConfigPage = () => {
    const [data, setData] = useState({
        legal_name: '', rfc: '', address: '', phone: '', email: '', website: '', logo_url: '',
        commission_receipt_legend: ''
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/company`, { headers: authHeaders() });
                setData({ ...data, ...res.data });
            } catch (e) {} finally { setLoading(false); }
        })();
        // eslint-disable-next-line
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            await axios.put(`${API}/company`, data, { headers: authHeaders() });
            toast.success('Datos de empresa guardados');
        } catch (err) {
            toast.error('Error al guardar');
        }
    };

    if (loading) return <div className="p-8 text-center text-[#8B8474]">Cargando...</div>;

    return (
        <div className="space-y-6 animate-fade-up max-w-3xl" data-testid="company-page">
            <div className="flex items-end justify-between pb-6 border-b border-[#ECE3D2]">
                <div>
                    <div className="flex items-center gap-2 text-xs mb-2">
                        <span className="text-[#8B8474]">Configuración</span>
                        <span className="text-[#C7BCA5]">/</span>
                        <span className="text-[#1F2D4B] font-medium">Datos de la empresa</span>
                    </div>
                    <h1 className="font-display text-3xl font-bold text-[#1F2D4B]">Datos de la empresa</h1>
                    <p className="text-sm text-[#4B5878] mt-1.5">Información que aparece en recibos, comisiones y facturas</p>
                </div>
                <Buildings size={48} weight="duotone" className="text-[#8B8474] opacity-50" />
            </div>

            <form onSubmit={handleSave} className="bg-white border border-[#ECE3D2] rounded-lg p-6 space-y-4">
                <div>
                    <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Razón social *</Label>
                    <Input value={data.legal_name} onChange={(e) => setData({ ...data, legal_name: e.target.value })} required data-testid="legal-name-input" className="h-10 rounded-md border-[#ECE3D2]" placeholder="Exploremos S.A. de C.V." />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">RFC</Label>
                        <Input value={data.rfc} onChange={(e) => setData({ ...data, rfc: e.target.value.toUpperCase() })} data-testid="rfc-input" className="h-10 rounded-md border-[#ECE3D2] font-mono uppercase" placeholder="XAXX010101000" maxLength={13} />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Teléfono</Label>
                        <Input value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} data-testid="phone-input" className="h-10 rounded-md border-[#ECE3D2]" placeholder="+52 55 1234 5678" />
                    </div>
                </div>

                <div>
                    <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Dirección fiscal</Label>
                    <Textarea value={data.address} onChange={(e) => setData({ ...data, address: e.target.value })} data-testid="address-input" rows={2} className="rounded-md border-[#ECE3D2] resize-none" placeholder="Calle, número, colonia, ciudad, estado, CP" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Email corporativo</Label>
                        <Input type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} data-testid="company-email-input" className="h-10 rounded-md border-[#ECE3D2]" placeholder="contacto@exploremos.mx" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Sitio web</Label>
                        <Input value={data.website} onChange={(e) => setData({ ...data, website: e.target.value })} data-testid="website-input" className="h-10 rounded-md border-[#ECE3D2]" placeholder="exploremos.mx" />
                    </div>
                </div>

                <div className="pt-4 border-t border-[#ECE3D2]">
                    <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">
                        Leyenda del recibo de comisiones
                    </Label>
                    <Textarea
                        value={data.commission_receipt_legend || ''}
                        onChange={(e) => setData({ ...data, commission_receipt_legend: e.target.value })}
                        data-testid="commission-receipt-legend-input"
                        rows={5}
                        className="rounded-md border-[#ECE3D2] text-sm leading-relaxed"
                        placeholder="Recibí de Exploremos la cantidad de {net_amount} ({net_amount_words}) por concepto de comisiones del periodo correspondiente, quedando a satisfacción de ambas partes.&#10;&#10;— {seller_name} · {date}"
                    />
                    <p className="text-[11px] text-[#8B8474] mt-1.5">
                        Aparece como firma / acuse en el recibo de comisión. Variables disponibles:&nbsp;
                        <code className="bg-[#F8F4EC] px-1 rounded text-[#1F2D4B]">{'{seller_name}'}</code>,&nbsp;
                        <code className="bg-[#F8F4EC] px-1 rounded text-[#1F2D4B]">{'{net_amount}'}</code>,&nbsp;
                        <code className="bg-[#F8F4EC] px-1 rounded text-[#1F2D4B]">{'{net_amount_words}'}</code>,&nbsp;
                        <code className="bg-[#F8F4EC] px-1 rounded text-[#1F2D4B]">{'{date}'}</code>.
                    </p>
                </div>

                <div className="flex justify-end pt-4 border-t border-[#ECE3D2]">
                    <Button type="submit" data-testid="save-company-btn" className="h-10 px-4 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md font-medium btn-lift">
                        <FloppyDisk size={14} weight="bold" className="mr-1.5" /> Guardar cambios
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default CompanyConfigPage;
